import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { fetchLeaderboard, fetchMyRank } from '../net/api.js';
import { netManager } from '../net/index.js';
import { UI, makeButton, makeCloseButton } from '../utils/ui-button.js';

// ── Leaderboard — top players by Gold (mouse/touch-driven overlay). ──
// GET /leaderboard?limit=20 → top N players ordered by Gold DESC. Shows a loading state, then the
// fetched rows (rank #1/#2/#3 with gold/silver/bronze accents, username, right-aligned Gold). When
// the board is empty (offline / no players) it shows "No players yet". Opens over the paused world
// and resumes it on close (ESC also closes).

/**
 * @typedef LeaderboardSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

/** @typedef {import('../net/api.js').LeaderboardEntry} LeaderboardEntry */

/** Podium accents for the top three ranks (1 → gold, 2 → silver, 3 → bronze). */
const MEDAL = { 1: 0xf5b23e, 2: 0xc7d0d8, 3: 0xcd7f4a };
const MEDAL_HEX = { 1: '#f5b23e', 2: '#c7d0d8', 3: '#cd7f4a' };

export class LeaderboardScene extends BaseScene {
  /** @type {LeaderboardSceneData} */ #sceneData;
  /** @type {Phaser.GameObjects.Container} */ #rowsContainer;
  /** @type {Phaser.GameObjects.Text} */ #statusText;
  /** @type {number} */ #panelLeft = 0;
  /** @type {number} */ #panelW = 0;
  /** @type {number} */ #panelBottom = 0;
  /** @type {number} */ #rowsTop = 0;
  /** @type {import('../net/api.js').PlayerRank | null} */ #myRank = null;
  /** @type {Phaser.GameObjects.Container} */ #youContainer;

  constructor() {
    super({ key: SCENE_KEYS.LEADERBOARD_SCENE });
  }

  /** @param {LeaderboardSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    this.add.rectangle(0, 0, width, height, 0x05080d, 0.82).setOrigin(0);

    const panelW = Math.min(720, width - 60);
    const panelH = Math.min(620, height - 40);
    const cx = width / 2;
    const top = height / 2 - panelH / 2;
    const left = cx - panelW / 2;
    this.#panelLeft = left;
    this.#panelW = panelW;
    this.#panelBottom = top + panelH;

    const panel = this.add.graphics();
    panel.fillStyle(UI.glass, 0.96);
    panel.fillRoundedRect(left, top, panelW, panelH, 18);
    panel.lineStyle(2, UI.emerald, 0.6);
    panel.strokeRoundedRect(left, top, panelW, panelH, 18);

    this.add
      .text(cx, top + 40, 'LEADERBOARD', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    this.add
      .text(cx, top + 72, '🏆  TOP TAMERS BY GOLD', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '14px',
        color: UI.goldHex,
      })
      .setOrigin(0.5);

    makeCloseButton(this, left + panelW - 34, top + 34, () => this.#close());

    this.#rowsTop = top + 100;
    this.#rowsContainer = this.add.container(0, 0);
    this.#youContainer = this.add.container(0, 0);

    this.#statusText = this.add
      .text(cx, top + panelH / 2, 'Loading leaderboard…', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: UI.subHex,
      })
      .setOrigin(0.5);

    void this.#load();
    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  async #load() {
    const wallet = netManager?.wallet ?? null;
    const [rows, mine] = await Promise.all([fetchLeaderboard(20), fetchMyRank(wallet)]);
    this.#myRank = mine;
    this.#render(rows);
    this.#renderYou();
  }

  /**
   * Pin a highlighted "YOU — #rank · gold G" row at the bottom (gold accent), even when the player
   * is outside the visible top rows, plus a "Flex on X" share button. Hidden offline / walletless.
   */
  #renderYou() {
    this.#youContainer.removeAll(true);
    if (!this.#myRank) return;

    const x = this.#panelLeft + 30;
    const w = this.#panelW - 60;
    const rowH = 50;
    const shareH = 40;
    // Stack the share button under the YOU row, both pinned near the panel bottom.
    const shareY = this.#panelBottom - 28 - shareH / 2;
    const y = shareY - shareH / 2 - 10 - rowH;

    const card = this.add.graphics();
    card.fillStyle(0x1c1605, 0.92);
    card.fillRoundedRect(x, y, w, rowH, 10);
    card.lineStyle(2, UI.gold, 0.95);
    card.strokeRoundedRect(x, y, w, rowH, 10);
    this.#youContainer.add(card);

    this.#youContainer.add(
      this.add
        .text(x + 18, y + rowH / 2, `YOU · #${this.#myRank.rank}`, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '22px',
          color: UI.goldHex,
        })
        .setOrigin(0, 0.5)
    );
    this.#youContainer.add(
      this.add
        .text(x + w - 16, y + rowH / 2, `${(this.#myRank.gold ?? 0).toLocaleString()} 🪙  of ${this.#myRank.total}`, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '18px',
          color: UI.goldHex,
        })
        .setOrigin(1, 0.5)
    );

    this.#youContainer.add(
      makeButton(this, {
        x: this.#panelLeft + this.#panelW / 2,
        y: shareY,
        width: 240,
        height: shareH,
        label: '🐦 Flex on X',
        primary: true,
        fontSize: 18,
        onClick: () => this.#shareOnX(),
      })
    );
  }

  /** Open a prefilled tweet flexing the player's rank + gold (new tab). */
  #shareOnX() {
    const rank = this.#myRank?.rank ?? '?';
    const gold = (this.#myRank?.gold ?? 0).toLocaleString();
    const text =
      `I'm rank #${rank} on Monstara with ${gold} Gold 🏆 ` +
      `Catch monsters, earn Gold, redeem REAL graded Pokémon cards. Play free 👉 monstara.fun $MONSTARA @PlayMonstara`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  }

  /** @param {LeaderboardEntry[]} rows */
  #render(rows) {
    this.#rowsContainer.removeAll(true);

    if (!rows.length) {
      this.#statusText.setText('No players yet');
      return;
    }
    this.#statusText.setText('');

    const x = this.#panelLeft + 30;
    const w = this.#panelW - 60;
    const rowH = 50;
    const gap = 8;
    // Cap visible rows so the list never overflows into the pinned "YOU" row + share button.
    const maxRows = Math.min(rows.length, 7);

    rows.slice(0, maxRows).forEach((entry, i) => {
      const y = this.#rowsTop + i * (rowH + gap);
      const medal = MEDAL[entry.rank];
      const accent = medal ?? UI.glassStroke;
      const accentHex = MEDAL_HEX[entry.rank] ?? UI.textHex;

      const card = this.add.graphics();
      card.fillStyle(0x0d1c14, 0.85);
      card.fillRoundedRect(x, y, w, rowH, 10);
      card.lineStyle(2, accent, medal ? 0.95 : 0.6);
      card.strokeRoundedRect(x, y, w, rowH, 10);
      this.#rowsContainer.add(card);

      // rank badge (#1 #2 #3 colored)
      this.#rowsContainer.add(
        this.add
          .text(x + 18, y + rowH / 2, `#${entry.rank}`, {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: medal ? '26px' : '22px',
            color: accentHex,
          })
          .setOrigin(0, 0.5)
      );

      // username
      this.#rowsContainer.add(
        this.add
          .text(x + 90, y + rowH / 2, entry.username || 'Tamer', {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '20px',
            color: UI.textHex,
          })
          .setOrigin(0, 0.5)
      );

      // gold amount (right-aligned with coin)
      this.#rowsContainer.add(
        this.add
          .text(x + w - 16, y + rowH / 2, `${(entry.gold ?? 0).toLocaleString()} 🪙`, {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '20px',
            color: UI.goldHex,
          })
          .setOrigin(1, 0.5)
      );
    });
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  #close() {
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.LEADERBOARD_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (this.scene.isSleeping(prev)) {
      // Slept previous scene: wake (not restart) it, otherwise handleSceneResume never fires
      // and the player stays input-locked.
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
    // Always release input — only the resume() path fires handleSceneResume.
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
